import React from 'react';
import Grid from '@mui/material/Grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import Panel from 'components/Panel';
import {
   IconButton,
   List,
   ListItem,
   ListItemButton,
   ListItemIcon,
   ListItemText,
   Typography,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import {colors} from 'common/colors';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import FormikTextField from 'components/inputs/FormikTextField';
import Button from 'components/Button';
import {useTranslation} from 'react-i18next';
import {VFSGroupNames} from 'types/diplomat/sftp/VFSGroup';
import {useGetApiCallback} from 'common/hooks/useGetApiCallback';
import {useFieldValue} from 'components/hook-form/useFieldValue';
import {IVFSGroup} from 'types/diplomat/sftp/VFSGroup';
import api from 'api';
import Loader, {LoaderInline} from 'components/Loader';
import {IDiplomatObject} from 'types/DiplomatObject';
import Dialog from 'components/Dialog';
import {useModal} from 'common/hooks/useModal';
import {ReadOnlyProvider} from 'common/context/ReadOnlyContext';
import {GridCentered} from 'components/GridCentered';
import {useIsLdapConfigured} from '../../features/layout/CommonPage/panels/SFTP/hooks/useIsLdapConfigured';
import {useOpenSubDialog} from 'features/layout/redux/hooks/useOpenSubDialog';
import {DialogTypes} from 'common/names';
import {
   CancelableConfirmationData,
   CancelableConfirmationDialogResult,
} from 'components/dialogs/CancelableConfirmationDialog';
import {useFormContext} from 'react-hook-form';
import {usePassThroughProps, useTriggerSubmitForm} from 'common/context/FormContext';
import {usePostApi} from 'common/hooks/usePostApi';

const ListWrapper = ({items}: {items?: IDiplomatObject[]}) => {
   const {t} = useTranslation();
   return (
      <List sx={{width: '100%', bgcolor: 'background.paper', height: 200, overflowY: 'scroll'}}>
         {items ? (
            items.map((item) => (
               <ListItem disablePadding key={item.name}>
                  <ListItemButton
                     role={undefined}
                     //    onClick={() => select(role)}
                     dense
                     //    selected={
                     //       role.objectType === selected?.objectType && role.id === selected?.id
                     //    }
                  >
                     <ListItemIcon sx={{minWidth: 'auto', paddingRight: '1rem'}}>
                        <GroupIcon />
                     </ListItemIcon>
                     <ListItemText id={item.name} primary={item.name} />
                  </ListItemButton>
               </ListItem>
            ))
         ) : (
            <Typography>{t('No Data')}</Typography>
         )}
      </List>
   );
};

export type LDAPMapPanelPassThroughProps = {
   loading: boolean;
   ldapMembers: IDiplomatObject[];
   setRefreshLdapRequired: (s: boolean) => void;
   refreshldap: () => void;
};

export const LDAPmapPanel = (props: {
   title?: string;
   checkboxLabel?: string;
   content?: string;
   filterApi?: string;
}) => {
   const {t} = useTranslation();
   const {
      title = t('LDAP Server Connection'),
      checkboxLabel = t('Map LDAP groups to this Group'),
      content = t(
         "Specify the CN of an LDAP group, and that group's membership will be automatically added to this group."
      ),
      filterApi = api.vfsTestldap,
   } = props;

   const {open: filteredLdapUsersOpen, handleOpen, handleClose} = useModal();
   const isLdapConfigured = useIsLdapConfigured();

   const filter = useFieldValue<IVFSGroup>('ldapGroupFilter') as IVFSGroup['ldapGroupFilter'];
   const includeLdapUsers = useFieldValue<IVFSGroup>(
      'includeLdapUsers'
   ) as IVFSGroup['includeLdapUsers'];
   const {request: filterLdap, state: filteredLdapUsers, loading: filterLoading} = usePostApi<
      IDiplomatObject[]
   >(filterApi);

   const {
      formState: {dirtyFields},
   } = useFormContext<IVFSGroup>();

   const {
      loading,
      refreshldap: vfsRefreshldap,
      ldapMembers,
      setRefreshLdapRequired,
   } = usePassThroughProps<LDAPMapPanelPassThroughProps>();

   const triggerSubmit = useTriggerSubmitForm();
   const refreshConfirmHandler = (res: CancelableConfirmationDialogResult) => {
      // console.debug('Close dialog Result', res);
      if (res === CancelableConfirmationDialogResult.OK) {
         setRefreshLdapRequired(true);
         triggerSubmit();
      } else if (res === CancelableConfirmationDialogResult.NO) {
         vfsRefreshldap();
      }
   };
   const openRefreshConfirmation = useOpenSubDialog<CancelableConfirmationData>(
      DialogTypes.CancelableConfirmationDialog.id,
      {
         content: t('You have changed the filter value.\n Do you want to apply changes first?'),
      },
      undefined,
      undefined,
      refreshConfirmHandler
   );

   return (
      <Panel name={title}>
         <IconButton
            onClick={() => {
               if (dirtyFields['ldapGroupFilter']) {
                  openRefreshConfirmation();
               } else {
                  vfsRefreshldap();
               }
            }}
            disabled={!isLdapConfigured || !includeLdapUsers}
            sx={{
               position: 'absolute',
               right: '1rem',
               top: '0',
               color: colors.success,
               '&.disabled': {
                  color: 'text.disabled',
               },
            }}
         >
            <RefreshIcon />
         </IconButton>
         <Grid container spacing={2}>
            {content && (
               <Grid item sm={12}>
                  <Typography>{content}</Typography>
               </Grid>
            )}
            <Grid item sm={8}>
               <FormikCheckbox
                  disabled={!isLdapConfigured}
                  name={VFSGroupNames.includeLdapUsers}
                  label={checkboxLabel}
               />
            </Grid>
            <ReadOnlyProvider value={!isLdapConfigured || !includeLdapUsers}>
               <>
                  <Grid item sm={8}>
                     <FormikTextField
                        name={VFSGroupNames.ldapGroupFilter}
                        placeholder={'(memberOf=CN=GroupName,OU=Users,dc=coviant,dc=com)'}
                     />
                  </Grid>
                  <GridCentered item sm={2}>
                     <Button
                        disabled={!filter}
                        onClick={() => {
                           filterLdap(JSON.stringify({ldapFilter: filter}));
                           handleOpen();
                        }}
                     >
                        {filterLoading ? <LoaderInline /> : t('Test')}
                     </Button>
                  </GridCentered>

                  <Grid item xs={10}>
                     {loading && <Loader />}
                     {Boolean(ldapMembers?.length) && <ListWrapper items={ldapMembers} />}
                  </Grid>
               </>
            </ReadOnlyProvider>
         </Grid>

         <Dialog
            open={filteredLdapUsersOpen}
            header={t('LDAP Members')}
            close={handleClose}
            loading={filterLoading}
         >
            <ListWrapper items={filteredLdapUsers} />
         </Dialog>
      </Panel>
   );
};
